import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import useAuthStore from '../../stores/authStore';
import SessionTranscript from '../../components/SessionTranscript';
import {
  UserGroupIcon,
  UserIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';

/**
 * Leader Dashboard - For Directors and Supervisors
 * Shows team overview, organizational structure, scenario coverage, and session history
 */
const LeaderDashboard = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState(null);
  const [teamStats, setTeamStats] = useState(null);
  const [orgScenarios, setOrgScenarios] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [managerSessions, setManagerSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [filterSupervisor, setFilterSupervisor] = useState('all');

  const isDirector = user.role === 'director';

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      if (isDirector) {
        // Director sees entire organization
        const [orgResponse, statsResponse, scenariosResponse, supervisorsResponse] = await Promise.all([
          api.get(`/api/director/managers`),
          api.get(`/api/director/organization/stats`),
          api.get(`/api/director/scenarios`),
          api.get(`/api/director/supervisors`),
        ]);

        setTeamData(orgResponse.data.managers || []);
        setTeamStats(statsResponse.data.stats || statsResponse.data);
        setOrgScenarios(scenariosResponse.data.scenarios || []);
        setSupervisors(supervisorsResponse.data.supervisors || []);
      } else {
        // Supervisor sees their team
        const [teamResponse, statsResponse, scenariosResponse] = await Promise.all([
          api.get(`/api/supervisor/team`),
          api.get(`/api/supervisor/team-stats`),
          api.get(`/api/supervisor/scenarios`),
        ]);

        setTeamData(teamResponse.data.team || []);
        setTeamStats(statsResponse.data);
        setOrgScenarios(scenariosResponse.data.scenarios || []);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  };

  const loadManagerSessions = async (managerId) => {
    try {
      const response = await api.get(`/api/supervisor/manager/${managerId}`);
      setManagerSessions(response.data.recentSessions || []);
      setSelectedManager(managerId);
    } catch (error) {
      console.error('Error loading manager sessions:', error);
      toast.error('Не удалось загрузить сессии менеджера');
    }
  };

  const getScenarioCoverage = () => {
    if (!orgScenarios.length || !teamData?.length) return [];

    return orgScenarios.map((scenario) => {
      const managersWithAccess = teamData.filter((manager) =>
        manager.scenarios?.some((s) => s.id === scenario.id)
      );

      return {
        scenarioId: scenario.id || scenario.scenario_id,
        scenarioName: scenario.name,
        totalManagers: teamData.length,
        managersWithAccess: managersWithAccess.length,
        coverage: teamData.length > 0 ? (managersWithAccess.length / teamData.length) * 100 : 0,
      };
    });
  };

  // Get managers for a specific supervisor
  const getManagersForSupervisor = (supervisorId) => {
    if (!teamData) return [];
    return teamData.filter(m => m.supervisor_id === supervisorId);
  };

  // Get unassigned managers (no supervisor)
  const getUnassignedManagers = () => {
    if (!teamData) return [];
    return teamData.filter(m => !m.supervisor_id);
  };

  // Filter managers by selected supervisor
  const getFilteredManagers = () => {
    if (!teamData) return [];
    if (filterSupervisor === 'all') return teamData;
    if (filterSupervisor === 'unassigned') return getUnassignedManagers();
    return getManagersForSupervisor(filterSupervisor);
  };

  // Get supervisor stats computed from their managers
  const getSupervisorTeamStats = (supervisorId) => {
    const managers = getManagersForSupervisor(supervisorId);
    const totalSessions = managers.reduce((sum, m) => sum + (m.stats?.totalSessions || 0), 0);
    const completedSessions = managers.reduce((sum, m) => sum + (m.stats?.completedSessions || 0), 0);
    const managersWithScores = managers.filter(m => m.stats?.totalSessions > 0);
    const avgScore = managersWithScores.length > 0
      ? managersWithScores.reduce((sum, m) => sum + (m.stats?.averageScore || 0), 0) / managersWithScores.length
      : 0;
    return { managersCount: managers.length, totalSessions, completedSessions, avgScore };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const scenarioCoverage = getScenarioCoverage();
  const filteredManagers = getFilteredManagers();

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isDirector ? 'Дашборд директора' : 'Дашборд супервайзера'}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Обзор команды, статистика и управление доступом к сценариям
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <UserGroupIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm text-gray-600">Менеджеров</p>
              <p className="text-2xl font-bold text-gray-900">{teamData?.length || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm text-gray-600">Всего сессий</p>
              <p className="text-2xl font-bold text-gray-900">
                {teamStats?.total_sessions || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm text-gray-600">Завершено</p>
              <p className="text-2xl font-bold text-gray-900">
                {teamStats?.completed_sessions || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <TrophyIcon className="h-8 w-8 text-yellow-600" />
            <div className="ml-3">
              <p className="text-sm text-gray-600">Средний балл</p>
              <p className="text-2xl font-bold text-gray-900">
                {teamStats?.avg_score ? parseFloat(teamStats.avg_score).toFixed(1) : '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Supervisors Section - Director Only */}
      {isDirector && supervisors.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Супервайзеры</h2>
            <p className="text-sm text-gray-600">Руководители команд и их показатели</p>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {supervisors.map((supervisor) => {
                const svStats = getSupervisorTeamStats(supervisor.id);
                return (
                  <div
                    key={supervisor.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      filterSupervisor === supervisor.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                    onClick={() => setFilterSupervisor(
                      filterSupervisor === supervisor.id ? 'all' : supervisor.id
                    )}
                  >
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        <UserIcon className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div className="ml-3">
                        <div className="font-medium text-gray-900">
                          {supervisor.first_name} {supervisor.last_name}
                        </div>
                        <div className="text-xs text-gray-500">{supervisor.email}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Менеджеров:</span>{' '}
                        <span className="font-medium text-gray-900">{svStats.managersCount}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Сессий:</span>{' '}
                        <span className="font-medium text-gray-900">{svStats.totalSessions}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Завершено:</span>{' '}
                        <span className="font-medium text-gray-900">{svStats.completedSessions}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Ср. балл:</span>{' '}
                        <span className="font-medium text-gray-900">
                          {svStats.avgScore > 0 ? svStats.avgScore.toFixed(1) : '—'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
              {/* Unassigned managers card */}
              {getUnassignedManagers().length > 0 && (
                <div
                  className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                    filterSupervisor === 'unassigned'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300 hover:bg-gray-50'
                  }`}
                  onClick={() => setFilterSupervisor(
                    filterSupervisor === 'unassigned' ? 'all' : 'unassigned'
                  )}
                >
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <UserGroupIcon className="h-5 w-5 text-orange-600" />
                    </div>
                    <div className="ml-3">
                      <div className="font-medium text-gray-900">Без супервайзера</div>
                      <div className="text-xs text-gray-500">Не назначены</div>
                    </div>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Менеджеров:</span>{' '}
                    <span className="font-medium text-orange-700">{getUnassignedManagers().length}</span>
                  </div>
                </div>
              )}
            </div>
            {filterSupervisor !== 'all' && (
              <button
                onClick={() => setFilterSupervisor('all')}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Показать всех менеджеров
              </button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Scenario Coverage */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Покрытие сценариями</h2>
            <p className="text-sm text-gray-600">Сколько менеджеров имеют доступ к каждому сценарию</p>
          </div>
          <div className="p-4">
            {scenarioCoverage.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <DocumentTextIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>Нет доступных сценариев</p>
              </div>
            ) : (
              <div className="space-y-3">
                {scenarioCoverage.map((item) => (
                  <div key={item.scenarioId} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">{item.scenarioName}</span>
                      <span className="text-sm text-gray-600">
                        {item.managersWithAccess} / {item.totalManagers}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${item.coverage}%` }}
                      ></div>
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      {item.coverage.toFixed(0)}% покрытие
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Top Performers */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Лучшие результаты</h2>
            <p className="text-sm text-gray-600">Менеджеры с наивысшими баллами</p>
          </div>
          <div className="p-4">
            {!teamData || teamData.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <UserGroupIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>Нет данных о команде</p>
              </div>
            ) : (
              <div className="space-y-2">
                {[...teamData]
                  .filter(m => m.stats?.totalSessions > 0)
                  .sort((a, b) => (b.stats?.averageScore || 0) - (a.stats?.averageScore || 0))
                  .slice(0, 5)
                  .map((manager, index) => (
                    <div
                      key={manager.id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <div
                          className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                            index === 0
                              ? 'bg-yellow-100 text-yellow-700'
                              : index === 1
                              ? 'bg-gray-100 text-gray-700'
                              : index === 2
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {index + 1}
                        </div>
                        <div className="ml-3">
                          <div className="font-medium text-gray-900">
                            {manager.first_name} {manager.last_name}
                          </div>
                          <div className="text-xs text-gray-600">{manager.email}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-gray-900">
                          {manager.stats?.averageScore?.toFixed(1) || '—'}
                        </div>
                        <div className="text-xs text-gray-600">
                          {manager.stats?.totalSessions || 0} сессий
                        </div>
                      </div>
                    </div>
                  ))}
                {teamData.filter(m => m.stats?.totalSessions > 0).length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    Ещё нет завершённых тренировок
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Team Members Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {filterSupervisor !== 'all' ? 'Менеджеры (отфильтровано)' : 'Все менеджеры'}
              </h2>
              <p className="text-sm text-gray-600">
                {isDirector
                  ? 'Менеджеры организации, их статистика и назначенные сценарии'
                  : 'Все менеджеры и их назначенные сценарии'}
              </p>
            </div>
            <div className="text-sm text-gray-500">
              {filteredManagers.length} менеджер(ов)
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {filteredManagers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <UserGroupIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>
                {filterSupervisor !== 'all'
                  ? 'Нет менеджеров с выбранным фильтром'
                  : 'В команде нет менеджеров'}
              </p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Менеджер
                  </th>
                  {isDirector && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Супервайзер
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Сессии
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Средний балл
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Сценарии
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredManagers.map((manager) => (
                  <tr key={manager.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="font-medium text-gray-900">
                            {manager.first_name} {manager.last_name}
                          </div>
                          <div className="text-sm text-gray-600">{manager.email}</div>
                        </div>
                      </div>
                    </td>
                    {isDirector && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {manager.supervisor ? (
                          <div>
                            <div className="text-sm text-gray-900">
                              {manager.supervisor.first_name} {manager.supervisor.last_name}
                            </div>
                            <div className="text-xs text-gray-500">{manager.supervisor.email}</div>
                          </div>
                        ) : (
                          <span className="text-sm text-orange-600">Не назначен</span>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {manager.stats?.totalSessions || 0}
                      </div>
                      <div className="text-xs text-gray-600">
                        {manager.stats?.completedSessions || 0} завершено
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {manager.stats?.totalSessions > 0 ? (
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${
                            manager.stats.averageScore >= 70
                              ? 'text-green-700'
                              : manager.stats.averageScore >= 50
                              ? 'text-yellow-700'
                              : 'text-red-700'
                          }`}>
                            {manager.stats.averageScore.toFixed(1)}%
                          </span>
                          <div className="w-16 bg-gray-200 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full ${
                                manager.stats.averageScore >= 70
                                  ? 'bg-green-500'
                                  : manager.stats.averageScore >= 50
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(manager.stats.averageScore, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {manager.scenarios && manager.scenarios.length > 0 ? (
                          manager.scenarios.slice(0, 3).map((scenario) => (
                            <span
                              key={scenario.id}
                              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {scenario.name}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">Нет назначенных</span>
                        )}
                        {manager.scenarios && manager.scenarios.length > 3 && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            +{manager.scenarios.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => loadManagerSessions(manager.id)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Просмотр сессий
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Manager Sessions Modal */}
      {selectedManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">История сессий менеджера</h2>
                <button
                  onClick={() => {
                    setSelectedManager(null);
                    setManagerSessions([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {managerSessions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>У менеджера пока нет сессий</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {managerSessions.map((session) => (
                    <div
                      key={session.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors cursor-pointer"
                      onClick={() => setSelectedSessionId(session.id)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium text-gray-900">{session.scenario_name}</div>
                        <div className={`px-2 py-1 rounded text-xs font-medium ${
                          session.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : session.status === 'in_progress'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {session.status === 'completed' ? 'Завершено' :
                           session.status === 'in_progress' ? 'В процессе' : 'Не завершено'}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Дата:</span>{' '}
                          <span className="text-gray-900">
                            {new Date(session.created_at).toLocaleDateString('ru-RU')}
                          </span>
                        </div>
                        {session.score !== null && session.score !== undefined && (
                          <div>
                            <span className="text-gray-600">Балл:</span>{' '}
                            <span className="text-gray-900 font-medium">{session.score}</span>
                          </div>
                        )}
                        {session.status === 'completed' && (session.score === null || session.score === undefined) && (
                          <div>
                            <span className="text-gray-600">Балл:</span>{' '}
                            <span className="text-amber-700 font-medium">не выставлен</span>
                          </div>
                        )}
                        {session.duration_ms && (
                          <div>
                            <span className="text-gray-600">Длительность:</span>{' '}
                            <span className="text-gray-900">
                              {Math.floor(session.duration_ms / 60000)} мин
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="mt-3 text-sm text-blue-600 hover:text-blue-700">
                        Нажмите чтобы посмотреть диалог
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Session Transcript Modal */}
      {selectedSessionId && (
        <SessionTranscript
          sessionId={selectedSessionId}
          onClose={() => setSelectedSessionId(null)}
          userRole={user.role}
        />
      )}
    </div>
  );
};

export default LeaderDashboard;
